import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Title,
  Stack,
  Text,
  Group,
  Button,
  Badge,
  Divider,
  Alert,
  Loader,
  Card,
  Image,
  SimpleGrid,
} from '@mantine/core'
import { IconAlertCircle, IconLogout, IconAnchor } from '@tabler/icons-react'
import { useAppUser, useLogout } from '../api/account'
import { useBoats } from '../api/boats'

export default function ProfilePage() {
  const navigate = useNavigate()
  const { data: user, isPending, isError, error } = useAppUser()
  const { mutate: logout, isPending: isLoggingOut } = useLogout()
  const { data: boatsData, isPending: boatsPending } = useBoats()

  const handleLogout = () => {
    logout(undefined, {
      onSuccess: () => {
        navigate('/app/login')
      },
    })
  }

  if (isPending) {
    return (
      <Container size={600} my={40}>
        <Stack align="center" justify="center">
          <Loader />
        </Stack>
      </Container>
    )
  }

  if (isError || !user) {
    return (
      <Container size={600} my={40}>
        <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red">
          {error instanceof Error
            ? error.message
            : 'Failed to load user profile. Please login.'}
        </Alert>
        <Button mt="md" onClick={() => navigate('/app/login')}>
          Go to Login
        </Button>
      </Container>
    )
  }

  return (
    <Container size={600} my={40}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={2}>User Profile</Title>
          <Button
            variant="outline"
            color="red"
            leftSection={<IconLogout size={16} />}
            onClick={handleLogout}
            loading={isLoggingOut}
          >
            Logout
          </Button>
        </Group>

        <Paper withBorder shadow="sm" p="md" radius="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={500}>Email</Text>
              <Group gap="xs">
                <Text>{user.email}</Text>
                {user.is_email_verified ? (
                  <Badge color="green" size="sm">
                    Verified
                  </Badge>
                ) : (
                  <Badge color="red" size="sm">
                    Not Verified
                  </Badge>
                )}
              </Group>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text fw={500}>User ID</Text>
              <Text>{user.user_id}</Text>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text fw={500}>Name</Text>
              <Text>{user.profile.name || 'Not set'}</Text>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text fw={500}>Phone Number</Text>
              <Text>{user.profile.phone_number || 'Not set'}</Text>
            </Group>
          </Stack>
        </Paper>

        <Paper withBorder shadow="sm" p="md" radius="md">
          <Title order={4} mb="sm">
            Preferences
          </Title>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={500}>Date Format</Text>
              <Text>{user.options.date_format}</Text>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text fw={500}>Distance Units</Text>
              <Text>{user.options.distance_units}</Text>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text fw={500}>Temperature Units</Text>
              <Text>{user.options.temperature_units}</Text>
            </Group>
          </Stack>
        </Paper>

        <Paper withBorder shadow="sm" p="md" radius="md">
          <Title order={4} mb="sm">
            Notifications
          </Title>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={500}>Via Email</Text>
              <Badge color={user.notifications.via_email ? 'green' : 'gray'}>
                {user.notifications.via_email ? 'Enabled' : 'Disabled'}
              </Badge>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text fw={500}>Via SMS</Text>
              <Badge color={user.notifications.via_sms ? 'green' : 'gray'}>
                {user.notifications.via_sms ? 'Enabled' : 'Disabled'}
              </Badge>
            </Group>
          </Stack>
        </Paper>

        <Paper withBorder shadow="sm" p="md" radius="md">
          <Title order={4} mb="sm">
            My Boats
          </Title>

          {boatsPending ? (
            <Stack align="center" py="md">
              <Loader size="sm" />
            </Stack>
          ) : !boatsData || boatsData.boats.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No boats found
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {boatsData.boats.map((boat) => (
                <Card
                  key={boat.boat_id}
                  shadow="sm"
                  padding="sm"
                  radius="md"
                  withBorder
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/app/boats/${boat.boat_id}`)}
                >
                  <Card.Section>
                    {boat.photos ? (
                      <Image
                        src={boat.photos.thumbnail}
                        alt={boat.name}
                        h={120}
                        fit="cover"
                      />
                    ) : (
                      <Stack
                        align="center"
                        justify="center"
                        h={120}
                        bg="gray.1"
                      >
                        <IconAnchor size={48} color="gray" />
                      </Stack>
                    )}
                  </Card.Section>

                  <Stack gap="xs" mt="sm">
                    <Group justify="space-between">
                      <Text fw={500}>{boat.name}</Text>
                      <Badge size="sm">{boat.year_built}</Badge>
                    </Group>

                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        Length: {boat.length}m
                      </Text>
                      {boat.gps_device && (
                        <Badge size="sm" color="green" variant="light">
                          GPS
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Paper>
      </Stack>
    </Container>
  )
}
